import React from 'react';
import Button from '../../../../shared/components/Button/Button';
import Card from '../../../../shared/components/Card/Card';
import Chip from '../../../../shared/components/Chip/Chip';
import EmptyState from '../../../../shared/components/EmptyState/EmptyState';
import SearchInput from '../../../../shared/components/SearchInput/SearchInput';
import Typography from '../../../../shared/components/Typography/Typography';
import WikiArticleCard from './WikiArticleCard';
import { formatWikiDate } from '../wiki.date';
import {
  ArticleIcon,
  ChecklistIcon,
  FolderIcon,
  HistoryIcon,
  SearchIcon,
  getWikiCategoryVisual,
  getWikiDocumentVisual,
} from './WikiIcons';

const shortcuts = [
  { title: 'Procédures', type: 'Procédure', description: 'Méthodes et processus internes', terms: ['procédure'] },
  { title: 'Modes opératoires', type: 'Mode opératoire', description: 'Instructions pas à pas', terms: ['mode opératoire'] },
  { title: 'Fiches techniques', type: 'Fiche technique', description: 'Données et références métier', terms: ['fiche technique', 'technique'] },
  { title: 'Checklists', type: 'Checklist', description: 'Contrôles et points de vérification', terms: ['checklist'] },
  { title: 'Modèles', type: 'Modèle', description: 'Documents prêts à réutiliser', terms: ['modele'] },
  { title: 'Retours d\'expérience', type: 'Retour d\'expérience', description: 'Enseignements des projets', terms: ['retour d\'expérience', 'rex'] },
  { title: 'Référentiels', type: 'Référentiel', description: 'Normes et cadres applicables', terms: ['référentiel', 'norme'] },
];

const countShortcutArticles = (articles, terms) => articles.filter((article) => {
  const searchable = [article.title, article.type, article.tags, article.summary]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return terms.some((term) => searchable.includes(term));
}).length;

function WikiHomePage({
  articles,
  categories,
  favorites = [],
  getCategoryLabel,
  navigate,
  onOpenArticle,
  onSearchChange,
  search = '',
  onToggleFavorite,
  publishedArticles,
  recentArticles,
}) {
  const highlightedArticles = recentArticles.length ? recentArticles : publishedArticles.slice(0, 3);
  const draftCount = articles.filter((article) => article.status === 'Brouillon').length;
  const reviewCount = articles.filter((article) => ['À valider', 'A valider'].includes(article.status)).length;
  const runSearch = () => {
    const query = search.trim();
    navigate(query ? `/Wiki/search?q=${encodeURIComponent(query)}` : '/Wiki/search');
  };
  const statCards = [
    { label: 'Articles publiés', value: publishedArticles.length, path: '/Wiki/search?status=Publie', Icon: ArticleIcon, tone: 'teal' },
    { label: 'Brouillons', value: draftCount, path: '/Wiki/admin/drafts', Icon: FolderIcon, tone: 'blue' },
    { label: 'À valider', value: reviewCount, path: '/Wiki/admin/review', Icon: ChecklistIcon, tone: 'orange' },
    { label: 'Favoris', value: favorites.length, path: '/Wiki/favorites', Icon: HistoryIcon, tone: 'pink' },
  ];
  const activateStatCard = (event, path) => {
    if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
    if (event.type === 'keydown') event.preventDefault();
    navigate(path);
  };

  return (
    <div className="wiki-content-stack wiki-home-page">
      <section className="wiki-home-search">
        <div className="wiki-home-search__heading">
          <span className="wiki-home-search__icon wiki-icon-tone--pink" aria-hidden="true"><ArticleIcon /></span>
          <div>
            <Typography variant="h5">Base de connaissances</Typography>
            <p>Centralisez les procédures, méthodes, référentiels et retours d’expérience de l’entreprise.</p>
          </div>
        </div>
        <SearchInput
          actionLabel="Rechercher"
          fullWidth
          label="Recherche documentaire"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          onSubmit={runSearch}
          placeholder="Rechercher une procédure, une norme, un projet, un référent..."
        />
      </section>

      <section className="wiki-home-stats" aria-label="Indicateurs documentaires">
        {statCards.map(({ Icon, label, path, tone, value }) => (
          <article
            key={label}
            aria-label={`Voir ${label.toLowerCase()}`}
            role="button"
            tabIndex={0}
            onClick={(event) => activateStatCard(event, path)}
            onKeyDown={(event) => activateStatCard(event, path)}
          >
            <span className={`wiki-home-stat__icon wiki-icon-tone--${tone}`}><Icon fontSize="small" /></span>
            <div><strong>{value}</strong><small>{label}</small></div>
          </article>
        ))}
      </section>

      <section className="wiki-home-shortcuts">
        <Typography variant="h6" mb={2}>Raccourcis documentaires</Typography>
        <div className="wiki-shortcut-grid">
          {shortcuts.map((shortcut) => {
            const { Icon: ShortcutIcon, tone } = getWikiDocumentVisual(shortcut.type);
            const articleCount = countShortcutArticles(articles, shortcut.terms);
            return (
            <Card key={shortcut.title} className="wiki-shortcut-card" compact interactive onClick={() => navigate(`/Wiki/search?q=${encodeURIComponent(shortcut.title)}`)}>
              <span className={`wiki-shortcut-icon wiki-icon-tone--${tone}`} aria-hidden="true"><ShortcutIcon fontSize="small" /></span>
              <span className="wiki-shortcut-copy">
                <strong>{shortcut.title}</strong>
                <small>{shortcut.description}</small>
              </span>
              <Chip size="small" variant="neutral">{articleCount}</Chip>
            </Card>
            );
          })}
        </div>
      </section>

      <section className="wiki-home-resume">
        <div className="wiki-section-header">
          <Typography variant="h6">Reprendre le travail</Typography>
          <Button variant="secondary" size="small" iconStart={<HistoryIcon fontSize="small" />} onClick={() => navigate('/Wiki/recent')}>
            Voir
          </Button>
        </div>
        {highlightedArticles.length ? (
          <div className="wiki-card-grid">
            {highlightedArticles.slice(0, 1).map((article) => (
              <WikiArticleCard
                key={article.id}
                article={article}
                getCategoryLabel={getCategoryLabel}
                isFavorite={favorites.includes(article.id)}
                onOpen={onOpenArticle}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </div>
        ) : (
          <EmptyState title="Aucun article recent" icon={<SearchIcon fontSize="small" />}>
            Les derniers articles consultés apparaitront ici.
          </EmptyState>
        )}
      </section>

      <section className="wiki-home-categories">
        <Typography variant="h6" mb={2}>Catégories principales</Typography>
        <div className="wiki-category-grid">
          {categories.filter((category) => !category.parent).map((category) => {
            const { Icon: DomainIcon, tone } = getWikiCategoryVisual(category.slug || category.name);
            return (
            <Card key={category.slug} className="wiki-category-card" compact interactive onClick={() => navigate(`/Wiki/category/${category.slug}`)}>
              <span className={`wiki-category-card__icon wiki-icon-tone--${tone}`} aria-hidden="true"><DomainIcon fontSize="small" /></span>
              <span className="wiki-category-card__copy">
                <strong>{category.name}</strong>
                <small>{category.description || 'Articles et ressources documentaires'}</small>
              </span>
              <span className="wiki-category-card__count">
                <strong>{category.count}</strong>
                <small>article(s)</small>
              </span>
            </Card>
            );
          })}
        </div>
      </section>

      <section className="wiki-home-updates">
        <Typography variant="h6" mb={2}>Dernières mises à jour</Typography>
        <div className="wiki-update-list">
          {[...articles].sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''))).slice(0, 5).map((article) => (
            <Card key={article.id} className="wiki-update-card" compact interactive onClick={() => onOpenArticle(article)}>
              <span className="wiki-update-card__copy">
                <strong>{article.title}</strong>
                <small>{getCategoryLabel(article.category)} · {article.type}</small>
              </span>
              <time dateTime={article.updatedAt}>{formatWikiDate(article.updatedAt, 'Date inconnue')}</time>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

export default WikiHomePage;
